import { fabric } from 'fabric';
import { Component, ComponentRef, EventEmitter, Injector, Type } from '@angular/core';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';
import { defaults } from 'lodash';
import { map, distinctUntilChanged } from 'rxjs/operators';
import { UserService } from 'src/app/services/user.service';
import { FrameItem } from '../../../../models/session';
import { YCanvasItemObject } from '../../../common/y-session';
import {
  ScribbleType,
  SessionSharedDataService,
} from '../../../../services/session-shared-data.service';
import { DomListener } from '../../../../utilities/DomListener';
import { DomListenerFactoryService } from '../../../../services/dom-listener-factory.service';
import { YCanvas, YStaticCanvas } from '../../wb-canvas/fabric-utils';
import { FlagsService, FLAGS } from '../../../../services/flags.service';
import { ItemModel } from '../../../../models/itemModel';
import { BoardItemsInteractionsManager } from '../board-items-interactions-manager';
import { DeviceAndBrowserDetectorService } from '../../../../services/device-and-browser-detector.service';
import { BoundingRect, SessionsVptService } from '../../../../services/sessions-vpt.service';
import { MainToolbarService } from '../../wb-main-toolbar-v2/services/main-toolbar.service';
import { isObjectInCanvasViewport } from '../../../../utilities/canvas-viewport-utils';
import { BoardItemConfig, CreationContext } from './board-item-configurations';
import { FabricExtraProperties } from './board-items-manager';
import { nextAgentEntranceDelayMs, shouldAnimateAgentEntrance } from './agent-drawon';
import { BoardItemLockService } from './board-item-lock.service';
import { BoardItemInterface } from './board-item.interface';

interface BoardItemAngularComponent<T> extends Component {
  state: T;
  stateChanged: EventEmitter<any>;
  deleteItem?: EventEmitter<void>;
}

export const MAX_BOARD_ITEMS_COUNT = 8192;

export enum FabricControls {
  RotateBottomLeft1 = 'rotBL1',
  RotateBottomLeft2 = 'rotBL2',
  RotateBottomRight1 = 'rotBR1',
  RotateBottomRight2 = 'rotBR2',
  RotateTopLeft1 = 'rotTL1',
  RotateTopLeft2 = 'rotTL2',
  RotateTopRight1 = 'rotTR1',
  RotateTopRight2 = 'rotTR2',
  MiddleTop = 'mt',
  MiddleBottom = 'mb',
  MiddleLeft = 'ml',
  MiddleRight = 'mr',
  TopRight = 'tr',
  TopLeft = 'tl',
  BottomRight = 'br',
  BottomLeft = 'bl',
  MobileRotation = 'MobileRotation',
}

export const isRotationControl = (control: FabricControls) =>
  control.startsWith('rot') || control === FabricControls.MobileRotation;

// This type is for better typing,
// and should be used instead of BoardItemComponentClass since the BoardItemComponentClass is an anonymous class
export type BoardItem = fabric.Object & BoardItemInterface;

// Please make sure that whatever public method or property you want to add to the BoardItemComponentClass to add it to BoardItemComponentInterface as well
export function BoardItemComponentClass<
  T extends BoardItemConfig = BoardItemConfig,
  Y extends BoardItemAngularComponent<T> = BoardItemAngularComponent<T>,
>(BaseClass: typeof fabric.Object) {
  /**
   * Abstract base class for all board items in the canvas.
   * This class provides core functionality for managing board items including:
   * - Fabric.js integration for canvas rendering
   * - Angular component integration
   * - State management and synchronization
   * - Event handling for user interactions
   * - Lock/unlock functionality
   * - HTML/Fabric synchronization
   *
   * @template T - The type of configuration this board item uses (extends BoardItemConfig)
   * @template Y - The type of Angular component this board item uses
   */
  return class extends BaseClass implements FabricExtraProperties, BoardItemInterface {
    itemType: ItemModel;
    customSelectable: boolean;
    configurations: T;

    skipRemoteAddition?: boolean;
    skipRemoteDeletion?: boolean;
    boardUid: string;

    isEditing = false;
    multiSelectable = true;
    duplicatable = true; // can duplicate or copy and paste
    focusOnInsertion = false;
    // Please be aware when enabling this for any object as it will cause extra matrix calculations to be executed on each click on the canvas, for each type of board item it was enabled on.
    propagateCanvasMouseDownToObject = false;
    // Used when we want to optimize the performance of the item as this skips a lot of matrix transformation and DOM manipulation
    // Please pay attention that when setting this to true, the lifecycle of the component changes
    // and creating the HTML component does not happen in the `onFabricAdded` method anymore,
    // so any HTML listeners / DOM manipulations attached in that method won't work
    createHtmlWhenInViewport = false;

    readonly fabricEventsToUpdateHTMLCoordsOn = [
      'object:moving',
      'object:scaling',
      'object:rotating',
      'object:modified',
    ];

    public componentRef?: ComponentRef<Y>;

    protected keepHtmlSyncedWithFabric = true;
    private htmlInitialized = false;

    protected itemOrder$ = new BehaviorSubject<number>(0);
    readonly onStateUpdated$: Subject<void>;

    // Subscriptions
    private remoteUpdatesSubscription?: Subscription;
    private zIndexSubscription?: Subscription;
    private angularComponentStateChangesSubscription?: Subscription;
    private angularComponentDeleteSubscription?: Subscription;
    private nonEventedItemSubscription?: Subscription;

    // Bound methods
    private boundSyncCurrentObjectHtmlWithFabric = this.syncCurrentObjectHtmlWithFabric.bind(this);
    private boundSyncHtmlWithFabric = this.syncHtmlWithFabric.bind(this);
    private boundOnFabricAdded = this.onFabricAdded.bind(this);
    private boundOnFabricRemoved = this.onFabricRemoved.bind(this);
    private boundOnStateChanged = this.syncChangesWithServer.bind(this);
    private boundDeleteBoardItem = this.deleteBoardItem.bind(this);
    private boundMouseDown = this.onMouseDown.bind(this);
    private boundMouseUp = this.onMouseUp.bind(this);
    private boundMouseMove = this.onMouseMove.bind(this);
    private boundOnDeselection = this.onDeselection.bind(this);
    private boundOnMoving = this._onMoving.bind(this);
    private boundOnMoved = this.onMoved.bind(this);
    private boundOnDoubleClick = this.onDoubleClick.bind(this);
    private boundCanvasMouseDownPropagator =
      this.canvasMouseDownToFabricObjectPropagator.bind(this);

    // Services
    private boardItemLockService: BoardItemLockService;
    private domListenerFactoryService: DomListenerFactoryService;
    protected userService: UserService;
    protected flagsService: FlagsService;
    protected sharedDataService: SessionSharedDataService;
    protected interactionsManager: BoardItemsInteractionsManager;
    protected deviceAndBrowserDetectorService: DeviceAndBrowserDetectorService;
    protected sessionVptService: SessionsVptService;
    protected mainToolbarService: MainToolbarService;

    domListener: DomListener;

    AngularComponentClass?: Type<Y>;

    constructor(
      injector: Injector,
      itemType: ItemModel,
      configurations: T,
      private invisibleControls: FabricControls[],
      creationContext: CreationContext,
    ) {
      super({
        stroke: 'transparent',
        strokeWidth: 1,
        fill: '#fff',
        opacity: 0.01,
        centeredRotation: true,
        hasControls: true,
        perPixelTargetFind: false,
        top: configurations.top,
        left: configurations.left,
        width: configurations.width,
        height: configurations.height,
        scaleX: configurations.scaleX,
        scaleY: configurations.scaleY,
        angle: configurations.angle,
      });
      this.customSelectable = true;

      this.itemType = itemType;
      this.configurations = configurations;
      this.uid = configurations.uid;
      this.vuid = configurations.vuid;
      this.boardUid = configurations.boardId;
      this.ScribbleType = configurations.ScribbleType;

      this.domListenerFactoryService = injector.get(DomListenerFactoryService);
      this.flagsService = injector.get(FlagsService);
      this.boardItemLockService = injector.get(BoardItemLockService);
      this.domListener = this.domListenerFactoryService.createInstance();
      this.sharedDataService = injector.get<SessionSharedDataService>(SessionSharedDataService);
      this.userService = injector.get(UserService);
      this.interactionsManager = injector.get<BoardItemsInteractionsManager>(
        BoardItemsInteractionsManager,
      );
      this.deviceAndBrowserDetectorService = injector.get(DeviceAndBrowserDetectorService);
      this.sessionVptService = injector.get(SessionsVptService);
      this.mainToolbarService = injector.get<MainToolbarService>(MainToolbarService);

      this.onStateUpdated$ = new Subject<void>();

      this.setUpFabricObjectEventsListeners();
      this.configureLocalLockState();
    }

    private setUpFabricObjectEventsListeners() {
      this.on('added', this.boundOnFabricAdded);
      this.on('removed', this.boundOnFabricRemoved);
      this.on('modified', this.boundOnStateChanged);
      this.on('mousedown', this.boundMouseDown);
      this.on('mouseup', this.boundMouseUp);
      this.on('mousemove', this.boundMouseMove);
      this.on('deselected', this.boundOnDeselection);
      this.on('moving', this.boundOnMoving);
      this.on('moved', this.boundOnMoved);
      this.on('mousedblclick', this.boundOnDoubleClick);
    }

    protected setUpFabricCanvasListeners() {
      this.canvas?.on('object:moving', this.boundOnMoving);
      if (!this.isOnStaticCanvas()) {
        // Using mouse:down:before not just simply mouse:down to run the logic before other listeners
        // More specifically we want our check for the active toolbar to be accurate in case other listeners to mouse:down changed the active toolbar
        this.canvas?.on('mouse:down:before', this.boundCanvasMouseDownPropagator);
      }
    }

    protected onFabricAdded(): void {
      this.setUpControlsVisibility();
      this.setUpFabricCanvasListeners();

      this.setUpRemoteUpdatesListener();

      if (!this.createHtmlWhenInViewport || this.isInViewport()) {
        this.createHtmlComponent();
      } // else the html sync state listener will set it up when in the viewport

      this.setUpHtmlStateSyncListeners();
      this.setUpNonEventedItemsClicks();

      this.itemOrder$.next(this.configurations.order);
    }

    protected onHtmlAdded(): void {
      this.htmlInitialized = true;

      this.setUpPassingEventOnHtmlToFabric();
      this.setUpZIndexListener();

      this.bindAngularComponentInputs();
      this.bindAngularComponentOutputs();

      this.syncHtmlWithFabric();
      this.maybePlayAgentEntrance();
    }

    /** Play the "draw-on" entrance (left-to-right ink reveal) for an HTML item
     * (e.g. a KaTeX equation) the AI tutor just wrote. Pure CSS on the element —
     * animates clip-path/opacity only, never transform (syncHtmlWithFabric owns
     * the element's transform). No-op for non-agent / backfill items. */
    private maybePlayAgentEntrance(): void {
      // Never draw-on inside a board-switcher thumbnail (static preview canvas) —
      // only the live board animates. (Matches the canvas-object gate in the
      // in-bound-updates manager.)
      if ((this.canvas as YCanvas | YStaticCanvas)?.isStatic) {
        return;
      }
      if (!shouldAnimateAgentEntrance(this.userId)) {
        return;
      }
      const el = this.componentRef?.location?.nativeElement as HTMLElement | undefined;
      if (!el) {
        return;
      }
      // Stagger a burst of equations so they reveal one-by-one (not all at once).
      // `both` fill-mode on .agent-drawon holds the element fully clipped during the
      // delay, so it stays hidden until its turn.
      const delayMs = nextAgentEntranceDelayMs();
      el.style.animationDelay = delayMs > 0 ? `${delayMs}ms` : '';
      el.classList.add('agent-drawon');
      el.addEventListener(
        'animationend',
        () => {
          el.classList.remove('agent-drawon');
          el.style.animationDelay = '';
        },
        { once: true },
      );
    }

    protected setUpControlsVisibility(invisibleControls = this.invisibleControls) {
      let rotationEnabled = true;
      for (const control of Object.values(FabricControls)) {
        const shouldHide = invisibleControls.includes(control);
        this.setControlVisible(control, !shouldHide);

        rotationEnabled = rotationEnabled && !(shouldHide && isRotationControl(control));
      }

      this.canvas?.requestRenderAll();
    }

    private setUpRemoteUpdatesListener() {
      const canvas = this.getCanvas();
      if (!canvas) {
        return;
      }
      this.remoteUpdatesSubscription?.unsubscribe();
      this.remoteUpdatesSubscription = canvas.stateManager
        ?.onStateUpdated$<T>(this.configurations.uid)
        .subscribe(this._setStateFromConfigurations.bind(this));
    }

    protected createHtmlComponent(): void {
      const htmlContext = (this.canvas as YCanvas | YStaticCanvas).htmlContext;
      if (htmlContext && this.AngularComponentClass) {
        this.componentRef = htmlContext.viewContainer.createComponent(this.AngularComponentClass);
        if (this.componentRef) {
          // Adding it as a child to the htmlCanvas because the above line only adds it beside it
          htmlContext.htmlCanvas.nativeElement.appendChild(
            this.componentRef.location.nativeElement,
          );
          this.onHtmlAdded();
        }
      }
    }

    private setUpHtmlStateSyncListeners() {
      for (const event of this.fabricEventsToUpdateHTMLCoordsOn) {
        this.canvas?.on(event, this.boundSyncCurrentObjectHtmlWithFabric);
      }
      this.canvas?.on('vpt:changed', this.boundSyncHtmlWithFabric);
    }

    // Events Are either passed to fabric or are captured by the HTML above it which belong to the board item,
    // In some cases we need the events to always reach fabric, that's why when a pointer move event reach the HTML
    // we pass it to the fabric. Fixing SPAC-4942
    private setUpPassingEventOnHtmlToFabric() {
      const htmlElement = this.componentRef?.location.nativeElement;
      if (!htmlElement) {
        return;
      }
      // passing events to fabric even if the mouse is on the item (fixing: SPAC-4942)
      const canvasElm = document.getElementsByClassName('upper-canvas').item(0);
      this.domListener.add(
        htmlElement,
        'pointermove',
        (e) => {
          const event = new MouseEvent(
            this.flagsService.isFlagEnabled(FLAGS.POINTER_EVENTS_FABRIC)
              ? 'pointermove'
              : 'mousemove',
            { clientX: e.clientX, clientY: e.clientY },
          );
          canvasElm?.dispatchEvent(event);
        },
        true,
      );
    }

    private setUpZIndexListener() {
      this.zIndexSubscription?.unsubscribe();
      this.zIndexSubscription = this.getZIndexObservable$().subscribe((zIndex) => {
        const htmlElement = this.componentRef?.location?.nativeElement;
        if (htmlElement) {
          htmlElement.style.zIndex = zIndex;
        }
      });
    }

    private setUpNonEventedItemsClicks() {
      if (this.isOnStaticCanvas()) {
        return;
      }
      this.nonEventedItemSubscription?.unsubscribe();
      this.nonEventedItemSubscription = this.interactionsManager
        .getNonEventedItemClickObservable(this.uid)
        .subscribe(() => {
          this.onCanvasMouseDown(false);
        });
    }

    bindAngularComponentOutputs() {
      const instance = this.componentRef?.instance;
      if (!instance) {
        return;
      }
      this.angularComponentStateChangesSubscription?.unsubscribe();
      this.angularComponentStateChangesSubscription = instance.stateChanged.subscribe(
        this.boundOnStateChanged,
      );

      this.angularComponentDeleteSubscription?.unsubscribe();
      if (instance.deleteItem) {
        this.angularComponentDeleteSubscription = instance.deleteItem.subscribe(
          this.boundDeleteBoardItem,
        );
      }
    }

    private _setStateFromConfigurations(configurations: T) {
      this.setStateFromConfigurations(configurations);
      this.onStateUpdated$.next();
    }

    protected setStateFromConfigurations(configurations: T) {
      this.configurations = defaults(configurations, this.configurations);

      (this as fabric.Rect)
        .set({
          top: configurations.top,
          left: configurations.left,
          width: configurations.width,
          height: configurations.height,
          scaleX: configurations.scaleX,
          scaleY: configurations.scaleY,
          angle: configurations.angle,
        })
        .setCoords();
      this.configureLocalLockState();
      this.syncHtmlWithFabric();
      this.bindAngularComponentInputs();

      this.itemOrder$.next(configurations.order);
    }

    public syncHtmlWithFabric() {
      this.syncCurrentObjectHtmlWithFabric({
        target: this,
      });
    }

    protected syncCurrentObjectHtmlWithFabric(
      e: fabric.IEvent<Event> | { target: fabric.Object },
    ): void {
      if (!e.target || this.width === undefined || this.height === undefined) {
        return;
      }

      // only if the current object or a group (selection) containing the object changes
      if (e.target !== this && e.target !== this.group) {
        return;
      }

      if (!this.keepHtmlSyncedWithFabric) {
        return;
      }

      if (this.createHtmlWhenInViewport && !this.htmlInitialized && this.isInViewport()) {
        this.createHtmlComponent();
      }

      const htmlElement = this.componentRef?.location?.nativeElement;
      const vpt = this.canvas?.viewportTransform; // accounts for the zooming and panning on the board
      if (!htmlElement || !vpt) {
        return;
      }

      const objectTransform = fabric.util.multiplyTransformMatrices(
        vpt,
        this.calcTransformMatrix(),
      );

      const transformationComponents = fabric.util.qrDecompose(objectTransform);

      // viewport transform scale and object transform scale are treated differently
      // the viewport one we want it to scale the object using scale() css style to have it appear as if it was zoomed in or out
      // the object one we want it to actually stretch the dimensions of the object to get the actual dimensions
      const vptScaleX = Math.sqrt(vpt[0] ** 2 + vpt[1] ** 2);
      const vptScaleY = Math.sqrt(vpt[2] ** 2 + vpt[3] ** 2);
      const scaleX = transformationComponents.scaleX / vptScaleX;
      const scaleY = transformationComponents.scaleY / vptScaleY;

      // TODO <Hatahet> should we use angular style binding? to make this more efficient?
      htmlElement.style.pointerEvents = 'none'; // giving control over pointer events to the child component
      htmlElement.style.position = 'absolute';
      htmlElement.style.width = `${this.width * Math.abs(scaleX)}px`;
      htmlElement.style.height = `${this.height * Math.abs(scaleY)}px`;
      htmlElement.style.left = `${transformationComponents.translateX}px`;
      htmlElement.style.top = `${transformationComponents.translateY}px`;
      htmlElement.style.transform = 'translate(-50%, -50%)';
      htmlElement.style.transform += `rotate(${transformationComponents.angle}deg)`;
      // Not applying the scaleX and scaleY directly, because we don't want to stretch the content, as they account for the actual width and height instead.
      // The vpt scale is the one responsible on the scaling we want to apply
      // We are just accounting for the scaling flip that the scaleX and scaleY indicates be setting the vpt scale to negative or positive value
      htmlElement.style.transform += `scaleX(${scaleX < 0 ? -vptScaleX : vptScaleX}) scaleY(${
        scaleY < 0 ? -vptScaleY : vptScaleY
      })`;
      htmlElement.style.transformOrigin = 'center';
    }

    protected deSyncHtmlWithFabric(): void {
      const htmlElement = this.componentRef?.location?.nativeElement;
      if (!htmlElement) {
        return;
      }
      htmlElement.style.width = 'initial';
      htmlElement.style.height = 'initial';
      htmlElement.style.left = '0';
      htmlElement.style.top = '0';
      htmlElement.style.transform = 'initial';
      htmlElement.style.transformOrigin = 'initial';
    }

    public syncChangesWithServer() {
      // TODO <Hatahet> check usages and see if we can call the changeState method instead
      this.getCanvas()?.stateManager.updateRemoteState(this);
    }

    public changeState(state: Partial<T>) {
      this.configurations = defaults(state, this.configurations);
      // TODO <Hatahet> optimize by not calling all of the next methods if their relevant variables didn't change
      this.itemOrder$.next(this.configurations.order);
      this.syncChangesWithServer();
      this.configureLocalLockState();
      this.syncHtmlWithFabric();
      this.bindAngularComponentInputs();
    }

    protected onMouseDown(e) {
      if (this.propagateCanvasMouseDownToObject) {
        return;
      }
      this.showLockSnackBarIfObjectLocked();
    }

    protected canvasMouseDownToFabricObjectPropagator(e: fabric.IEvent) {
      // This should only be done for a selected types of items,
      // as calling `containsPoint` for each board item on the board can be expensive, because it does matrix transformations
      if (!this.propagateCanvasMouseDownToObject) {
        return;
      }

      // To not interrupt other tools functionality, because this is only for selection which only done using the pointer tool
      if (this.mainToolbarService.activeToolName !== 'pointer') {
        return;
      }

      const { x, y } = this.canvas?.getPointer(e.e) ?? {};
      if (x === undefined || y === undefined) {
        return;
      }

      const point = new fabric.Point(x, y);
      const eventInsideCurrentObject = this.containsPoint(point, false, true);

      if (!eventInsideCurrentObject) {
        return;
      }

      let mouseDownAffectedOtherObjects = false;
      const target = e.target;
      if (target) {
        if (target.type === 'activeSelection') {
          // We always handle the clicks that are on a selection
          mouseDownAffectedOtherObjects = true;
        } else {
          // if the click affected another object that is evented, we should check which on should take the mouse down event based on the stacking order
          const currentObjects = this.canvas?.getObjects() ?? [];
          const currentObjectIndex = currentObjects.indexOf(this);
          const targetObjectIndex = currentObjects.indexOf(target);
          mouseDownAffectedOtherObjects = targetObjectIndex > currentObjectIndex;
        }

        // if the click should solely be on the current object, we should consider that case of having an object below the current one
        // This way we need to stop the interactions on it now the interactions should be on our object, to do that we dispatch a mouse up event
        if (!mouseDownAffectedOtherObjects) {
          (this.canvas as any)._onMouseUp(''); // removing the listeners from the object the initial click is currently interacting with
        }

        this.onCanvasMouseDown(mouseDownAffectedOtherObjects);
      } else {
        // We should check if the mouse down affected another non-evented item, so we rely on this service to handle it
        this.interactionsManager.onNonEventedItemClick(this);
      }
    }

    // Gets called even if the evented = false, meaning the object doesn't receive any event
    protected onCanvasMouseDown(mouseDownAffectedOtherObjects: boolean) {}

    protected onMouseUp(e) {}

    protected onMouseMove(e) {}

    protected _onMoving(e: fabric.IEvent): void {
      // When moving alone or when moving in a group
      if (e.target === this || e.target === this.group) {
        this.onMoving();
      }
    }

    protected onMoving(): void {}

    protected onMoved(): void {}

    protected onDoubleClick(): void {}

    protected onDeselection(): void {}

    public deleteBoardItem() {
      this.canvas?.remove(this);
    }

    // Currently this shouldn't be used, because we are transforming this into a frame item before storing it,
    // Will be used after everything (fabric objects) become BoardItem
    override toObject(propertiesToInclude?: string[]) {
      const toObjectParams = [...(propertiesToInclude ?? []), ...this.getPropertiesToInclude()];
      return super.toObject(toObjectParams);
    }

    getPropertiesToInclude(): string[] {
      return ['itemType', 'customSelectable', 'configurations'];
    }

    // A temporary format that we currently need because we are still storing data in the old structure of canvas items in the server
    public toFrameItem(): YCanvasItemObject {
      const yCanvasItemObject: YCanvasItemObject = new FrameItem(
        {
          _id: this.configurations.uid,
          position: new Map([
            ['top', this.top],
            ['left', this.left],
            ['width', this.width],
            ['height', this.height],
            ['scaleX', this.scaleX],
            ['scaleY', this.scaleY],
            ['index', this.configurations.order],
            ['angle', this.angle],
          ]),
          ownerId: this.configurations.ownerId,
          locked: this.configurations.locked,
        },
        false,
      );

      yCanvasItemObject.uid = this.uid;
      yCanvasItemObject.vuid = this.vuid;

      return yCanvasItemObject;
    }

    public configureLocalLockState() {
      if (this.configurations.locked) {
        this.closeLock();
        this.setUpControlsVisibility(Object.values(FabricControls));
      } else {
        this.openLock();
        this.setUpControlsVisibility();
      }
      // The lock state affects the lock state of the selection, and now that it changed, the selection needs re-evaluation
      this.interactionsManager.modifySelectionPropertiesBasedOnItsContent();
    }

    // This can be overridden if a component requires certain properties to stay locked when the object lock is opened
    protected openLock() {
      this.lockMovementX = false;
      this.lockMovementY = false;
      this.lockRotation = false;
      this.lockScalingX = false;
      this.lockScalingY = false;
      this.lockUniScaling = false;
      this.lockSkewingX = false;
      this.lockSkewingY = false;
      this.lockScalingFlip = false;
    }

    public closeLock() {
      this.lockMovementX = true;
      this.lockMovementY = true;
      this.lockRotation = true;
      this.lockScalingX = true;
      this.lockScalingY = true;
      this.lockUniScaling = true;
      this.lockSkewingX = true;
      this.lockSkewingY = true;
      this.lockScalingFlip = true;
    }

    public toggleLockState() {
      this.configurations.locked = !this.configurations.locked;
      this.syncChangesWithServer();
      this.configureLocalLockState();
      this.boardItemLockService.onLockStateChanged(this);
    }

    // Edit mode is for items that the user can interact with not just rotate, scale or move (the items with HTML content).
    // Edit mode can have different properties based on the board item, for example some board items can increase their z-index in edit mode, some can prevent moving the item .etc
    public shouldEnterEditMode(mouseY: number, itemY: number, itemSelected: boolean) {
      return false;
    }

    protected onFabricRemoved(): void {
      this.componentRef?.destroy();
      this.componentRef?.changeDetectorRef.detectChanges(); // because the above line  this will be called outside zone

      this.removeFabricEventsListeners();
      this.removeHtmlStateSyncListeners();

      this.domListener.clear();

      this.zIndexSubscription?.unsubscribe();
      this.remoteUpdatesSubscription?.unsubscribe();
      this.angularComponentDeleteSubscription?.unsubscribe();
      this.angularComponentStateChangesSubscription?.unsubscribe();
      this.itemOrder$.unsubscribe();
      this.nonEventedItemSubscription?.unsubscribe();
    }

    private removeFabricEventsListeners() {
      this.off('added', this.boundOnFabricAdded);
      this.off('removed', this.boundOnFabricRemoved);
      this.off('modified', this.boundOnStateChanged);
      this.off('mousedown', this.boundMouseDown);
      this.off('mouseup', this.boundMouseUp);
      this.off('mousemove', this.boundMouseMove);
      this.off('deselected', this.boundOnDeselection);
      this.off('moving', this.boundOnMoving);
      this.off('moved', this.boundOnMoved);
      this.off('mousedblclick', this.boundOnDoubleClick);

      this.canvas?.off('object:moving', this.boundOnMoving);
      if (!this.isOnStaticCanvas()) {
        this.canvas?.off('mouse:down:before', this.boundCanvasMouseDownPropagator);
      }
    }

    private removeHtmlStateSyncListeners() {
      for (const event of this.fabricEventsToUpdateHTMLCoordsOn) {
        this.canvas?.off(event, this.boundSyncCurrentObjectHtmlWithFabric);
      }
      this.canvas?.off('vpt:changed', this.boundSyncHtmlWithFabric);
    }

    public isOnStaticCanvas() {
      return !!(this.canvas as YCanvas | YStaticCanvas)?.isStatic;
    }

    protected getCanvas(): YStaticCanvas | YCanvas | undefined {
      return this.canvas as YCanvas;
    }

    public hasHtmlContent() {
      return !!this.componentRef;
    }

    // Those setters and getters exists so that BoardItems and plain fabric objects have them in the common interface between them
    public set vuid(newVUID: string | undefined) {
      this.configurations.vuid = newVUID;
    }

    public get vuid() {
      return this.configurations.vuid;
    }

    public set uid(newUID: string) {
      this.configurations.uid = newUID;
    }

    public get uid() {
      return this.configurations.uid;
    }

    public set userId(newUserId: string | undefined) {
      this.configurations.ownerId = newUserId ?? '';
    }

    public get userId() {
      return this.configurations.ownerId;
    }

    public set ScribbleType(newScribbleType: ScribbleType) {
      this.configurations.ScribbleType = newScribbleType;
    }

    public get ScribbleType() {
      return this.configurations.ScribbleType;
    }

    protected getDefaultZIndexObservable$(): Observable<number> {
      return this.itemOrder$.pipe(
        map((itemOrder) => {
          if (this.isOnStaticCanvas()) {
            return itemOrder;
          }
          const index = itemOrder - MAX_BOARD_ITEMS_COUNT - 1;
          return Math.min(-1, index);
        }),
        distinctUntilChanged(),
      );
    }

    protected bindAngularComponentInputs(): void {
      if (!this.componentRef) {
        return;
      }
      this.componentRef.setInput('state', this.configurations);
      this.componentRef.setInput('static', this.isOnStaticCanvas());
      this.componentRef.changeDetectorRef.detectChanges();
    }

    protected getZIndexObservable$(): Observable<number> {
      return this.getDefaultZIndexObservable$();
    }

    public handleInitializationFromPaste(deltaX: number, deltaY: number): void {
      // update the owner id to the current user id
      if (this.userService.userId) {
        this.configurations.ownerId = this.userService.userId;
      }
    }

    public isStoredAsFrameItem() {
      return true;
    }

    protected showLockSnackBarIfObjectLocked() {
      if (this.configurations.locked && this.mainToolbarService.activeToolName === 'pointer') {
        this.boardItemLockService.showLockSnackBar(this);
      }
    }

    getBoardItemBoundingRect(): BoundingRect {
      return {
        top: this.top ?? 0,
        left: this.left ?? 0,
        width: this.width ?? 0,
        height: this.height ?? 0,
      };
    }

    isInViewport() {
      const objCoords = {
        tl_x: this.left ?? 0,
        tl_y: this.top ?? 0,
        br_x: (this.left ?? 0) + (this.width ?? 0),
        br_y: (this.top ?? 0) + (this.height ?? 0),
      };

      // check the main canvas
      if (!this.isOnStaticCanvas()) {
        return this.sessionVptService.isObjectInViewport(objCoords);
      }

      // check the current canvas (static in preview)
      const canvas = this.canvas;
      if (!canvas) {
        return false;
      }
      return isObjectInCanvasViewport(canvas, objCoords);
    }
  };
}
